import { inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { TuiDialogService } from '@taiga-ui/core';
import { filter } from 'rxjs/operators';
import {
  RECORDING_NAVIGATION_BLOCKED_DIALOG,
} from '../components/recording-navigation-blocked-dialog/recording-navigation-blocked-dialog';
import { SpeechRecognitionService } from './speech-recognition.service';
import {
  sharedAudioCaptureStoppedMessage,
  TranscriptManagementService,
  UnexpectedStopEvent,
} from './transcript-management.service';

@Injectable({ providedIn: 'root' })
export class ActiveInterviewRecordingService {
  private readonly router = inject(Router);
  private readonly speechService = inject(SpeechRecognitionService);
  private readonly transcriptService = inject(TranscriptManagementService);
  private readonly dialogService = inject(TuiDialogService);

  private suppressNavigationBlock = 0;
  private confirmOpen = false;

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe((event) => this.handleNavigationStart(event));

    this.transcriptService.unexpectedStop$
      .pipe(filter((event) => !event.pending && event.reason === 'display-capture-ended'))
      .subscribe((event) => this.showSharedAudioCaptureStoppedDialog(event));
  }

  isRecordingForClient(
    clientId: string | null | undefined,
    evaluationTypeId: string | null | undefined
  ): boolean {
    if (!this.speechService.isRecording() || !clientId || !evaluationTypeId) {
      return false;
    }

    const meta = this.transcriptService.recordingMetadata();
    if (!meta) {
      return false;
    }

    return meta.patientId === clientId && meta.evaluationTypeId === evaluationTypeId;
  }

  /** Sidebar / programmatic navigation while recording (patient page is still active). */
  requestLeavePatientRecord(targetUrl: string): void {
    this.attemptLeavePatientRecord(targetUrl, { revertInFlightNavigation: false });
  }

  handleNavigationStart(event: NavigationStart): void {
    if (this.suppressNavigationBlock > 0) {
      this.suppressNavigationBlock--;
      return;
    }

    this.attemptLeavePatientRecord(event.url, { revertInFlightNavigation: true });
  }

  private attemptLeavePatientRecord(
    targetUrl: string,
    options: { revertInFlightNavigation: boolean }
  ): void {
    if (!this.speechService.isRecording()) {
      return;
    }

    const meta = this.transcriptService.recordingMetadata();
    if (!meta) {
      return;
    }

    const patientRecordPath = patientRecordBasePath(meta.patientId, meta.evaluationTypeId);
    const currentOnPatientPage = this.router.url.includes(patientRecordPath);
    const targetOnPatientPage = targetUrl.includes(patientRecordPath);

    if (!currentOnPatientPage || targetOnPatientPage) {
      return;
    }

    if (options.revertInFlightNavigation) {
      this.suppressNavigationBlock++;
      void this.router.navigateByUrl(this.router.url, { replaceUrl: true });
    }

    this.showCannotLeaveWhileRecordingDialog();
  }

  private showSharedAudioCaptureStoppedDialog(event: UnexpectedStopEvent): void {
    this.dialogService
      .open(
        sharedAudioCaptureStoppedMessage(event.saved, Boolean(event.capturedTranscript?.length)),
        {
          label: 'Audio Capture Stopped',
          size: 's',
          data: 'Ok',
        }
      )
      .subscribe();
  }

  showCannotLeaveWhileRecordingDialog(): void {
    if (this.confirmOpen) {
      return;
    }

    this.confirmOpen = true;

    this.dialogService
      .open<boolean>(RECORDING_NAVIGATION_BLOCKED_DIALOG, {
        label: 'Recording in progress',
        size: 'm',
        dismissible: false,
        closable: false,
      })
      .subscribe({
        next: () => {
          this.confirmOpen = false;
        },
        error: () => {
          this.confirmOpen = false;
        },
      });
  }
}

function patientRecordBasePath(patientId: string, evaluationTypeId: string): string {
  return `/client/${patientId}/evaluation-type/${evaluationTypeId}`;
}
